<template>
  <div class="calendly-share-container">
    <!-- Main Profile Link Card -->
    <div v-if="masterLink" class="dc-card master-card mb-4">
      <div class="p-3 p-md-4">
        <div class="d-flex align-items-start mb-3">
          <div class="dc-avatar-box mr-3" v-if="masterLink.avatarUrl">
            <img :src="masterLink.avatarUrl" :alt="masterLink.title" class="dc-avatar-img rounded-circle" />
          </div>
          <div class="dc-icon-box mr-3" v-else>
            <i class="fa fa-calendar-check-o text-white"></i>
          </div>
          <div>
            <h4 class="dc-card-title mb-1">{{ masterLink.title }}</h4>
            <p v-if="masterLink.description" class="dc-card-desc mb-0">
              {{ masterLink.description }}
            </p>
          </div>
        </div>

        <!-- Master URL Row & Action -->
        <div class="row pt-2 align-items-center">
          <div class="col-12">
            <div class="d-flex align-items-center dc-input-btn-group">
              <input type="text" readonly class="form-control dc-rounded-input" :value="masterLink.url"
                ref="masterInput" @focus="$event.target.select()" aria-label="Main Booking URL" />
              <button type="button" class="btn dc-navy-btn" :class="{ 'btn-copied': copiedKey === 'master' }"
                @click="copyUrl(masterLink.url, 'master', $refs.masterInput)">
                <i :class="copiedKey === 'master' ? 'fa fa-check' : 'fa fa-clipboard'" class="mr-1"></i>
                {{ copiedKey === 'master' ? 'Copied!' : 'Copy Link' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section Sub-Header -->
    <div class="dc-section-header mb-3 d-flex align-items-center justify-content-between flex-wrap">
      <div>
        <h4 class="dc-heading mb-1">
          <i class="fa fa-clock-o mr-1"></i> Meeting & Coaching Links
        </h4>
        <p class="text-muted small mb-0">
          Direct scheduling links for each onboarding session and 1-on-1 coaching call.
        </p>
      </div>
      <div class="d-flex align-items-center mt-2 mt-sm-0">
        <button type="button" class="btn btn-outline-secondary btn-sm refresh-btn" @click="fetchDynamicLinks"
          :disabled="isLoading" title="Refresh booking links from Calendly">
          <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': isLoading }"></i>
          {{ isLoading ? 'Syncing...' : 'Sync Links' }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <i class="fa fa-circle-o-notch fa-spin fa-2x text-primary mb-2"></i>
      <p class="text-muted small">Loading live booking links from Calendly...</p>
    </div>

    <!-- Event Cards Grid -->
    <div v-else class="row">
      <div v-for="(event, index) in events" :key="event.id || index" class="col-12 col-lg-6 mb-4 d-flex">
        <div class="dc-card event-card w-100 d-flex flex-column justify-content-between">
          <div class="p-3">
            <div class="mb-2 d-flex align-items-center justify-content-between flex-wrap">
              <h5 class="dc-event-title mb-0">
                <i :class="event.icon" class="mr-2 dc-icon-blue"></i>
                {{ event.title }}
              </h5>
              <span v-if="event.duration" class="badge badge-light border text-muted small mt-1 mt-sm-0">
                {{ event.duration }}
              </span>
            </div>

            <p class="dc-event-desc mb-0">
              {{ event.description }}
            </p>
          </div>

          <!-- URL Input & Navy Action Button -->
          <div class="p-3 bg-light-section border-top-light">
            <div class="d-flex align-items-center dc-input-btn-group">
              <input type="text" readonly class="form-control dc-rounded-input" :value="event.url"
                :ref="'input_' + index" @focus="$event.target.select()" aria-label="Event URL" />
              <button type="button" class="btn dc-navy-btn" :class="{ 'btn-copied': copiedKey === index }"
                @click="copyUrl(event.url, index, $refs['input_' + index])">
                <i :class="copiedKey === index ? 'fa fa-check' : 'fa fa-clipboard'" class="mr-1"></i>
                {{ copiedKey === index ? 'Copied!' : 'Copy Link' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CalendlyAPI from "@/api/calendly";

export default {
  name: "ShareBookingLink",
  data() {
    return {
      isLoading: true,
      isConnected: false,
      copiedKey: null,
      copyTimeout: null,
      masterLink: null,
      events: [],
    };
  },
  async mounted() {
    await this.fetchDynamicLinks();
  },
  methods: {
    async fetchDynamicLinks() {
      const token = CalendlyAPI.getToken();
      if (!token || token.startsWith("dummy_")) {
        this.isConnected = false;
        return;
      }

      this.isLoading = true;
      try {
        const user = await CalendlyAPI.getCurrentUser();
        if (user && user.uri) {
          this.isConnected = true;
          this.masterLink = {
            title: user.name,
            url: user.scheduling_url,
            avatarUrl: user.avatar_url,
            description:
              user.description ||
              "Welcome to the DC Trainings scheduling page. Please follow the instructions to add an appointment for training.",
          };

          const eventTypes = await CalendlyAPI.fetchEventTypes(user.uri);
          if (eventTypes && eventTypes.length > 0) {
            const sortedEvents = [...eventTypes]
              .filter((et) => et.active !== false)
              .sort((a, b) => {
                const getScore = (name) => {
                  if (name.includes("#1")) return 1;
                  if (name.includes("#2")) return 2;
                  if (name.includes("#3")) return 3;
                  if (name.includes("#4")) return 4;
                  if (name.toLowerCase().includes("coaching")) return 5;
                  return 6;
                };
                return getScore(a.name) - getScore(b.name);
              });

            const getIcon = (name) => {
              const lower = name.toLowerCase();
              if (lower.includes("#1")) return "fa fa-flag-checkered";
              if (lower.includes("#2")) return "fa fa-users";
              if (lower.includes("#3")) return "fa fa-line-chart";
              if (lower.includes("#4")) return "fa fa-bullhorn";
              if (lower.includes("coaching")) return "fa fa-clock-o";
              return "fa fa-calendar-check-o";
            };

            const getCleanDesc = (et) => {
              if (et.description_plain && et.description_plain.trim()) {
                const lines = et.description_plain
                  .split("\n")
                  .map((l) => l.trim())
                  .filter((l) => l.length > 0 && !l.startsWith("http"));
                if (lines.length > 0) {
                  return lines.slice(0, 2).join(" • ");
                }
              }
              return `Duration: ${et.duration || 30} minutes. Book directly on Calendly.`;
            };

            this.events = sortedEvents.map((et, index) => ({
              id: et.slug || et.uri || index,
              title: et.name,
              url: et.scheduling_url,
              duration: et.duration ? `${et.duration} min` : null,
              description: getCleanDesc(et),
              icon: getIcon(et.name),
            }));
          }
        }
      } catch (err) {
        console.warn("[Calendly] Error fetching dynamic booking links:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async copyUrl(url, key, inputRef) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const targetInput = Array.isArray(inputRef) ? inputRef[0] : inputRef;
          if (targetInput) {
            targetInput.select();
            document.execCommand("copy");
          }
        }

        this.copiedKey = key;
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.copiedKey = null;
        }, 2500);
      } catch (err) {
        console.error("Copy failed", err);
        if (this.$swal) {
          this.$swal({
            type: "error",
            title: "Copy Failed",
            text: "Please copy the URL directly from the field.",
          });
        }
      }
    },
  },
  beforeDestroy() {
    if (this.copyTimeout) clearTimeout(this.copyTimeout);
  },
};
</script>

<style scoped>
/* DC Trainings Design System */
.calendly-share-container {
  font-family: "Calibri", "Segoe UI", Arial, sans-serif;
  color: #343434;
}

/* Card Foundations */
.dc-card {
  background-color: #fff;
  border: 1px solid #e6e8ea;
  border-radius: 6px;
  position: relative;
  transition: all 0.2s ease;
}

.master-card {
  border-top: 3px solid #fbbb29;
  background: #fdfdfd;
}

.event-card {
  border-top: 3px solid #305397;
}

.event-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(48, 83, 151, 0.08);
}

.dc-card-title {
  font-family: "Calibri", sans-serif;
  font-size: 19px;
  font-weight: 600;
  color: #305397;
}

.dc-card-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.dc-heading {
  font-family: "Calibri", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #305397;
}

.dc-event-title {
  font-family: "Calibri", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #305397;
  line-height: 1.3;
}

.dc-event-desc {
  font-size: 13.5px;
  color: #666;
  line-height: 1.4;
  min-height: 38px;
}

.dc-avatar-box {
  width: 48px;
  height: 48px;
  min-width: 48px;
}

.dc-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 2px solid #fbbb29;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.dc-icon-box {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background-color: #305397;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.dc-icon-blue {
  color: #305397;
}

.border-top-light {
  border-top: 1px solid #e6e8ea;
}

.bg-light-section {
  background-color: #fafbfc;
}

/* Side-by-Side Input & Button Group */
.dc-input-btn-group {
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  gap: 8px !important;
}

.dc-rounded-input {
  flex: 1 1 auto !important;
  border: 1px solid #ced4da !important;
  border-radius: 5px !important;
  font-size: 13px !important;
  height: 40px !important;
  padding: 0 12px !important;
  background-color: #ffffff !important;
  box-shadow: none !important;
  margin: 0 !important;
}

.dc-navy-btn {
  height: 40px !important;
  padding: 0 16px !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  border-radius: 5px !important;
  background-color: #305397 !important;
  border: 1px solid #305397 !important;
  color: #ffffff !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap !important;
  transition: all 0.2s ease;
  margin: 0 !important;
  box-shadow: 0 1px 2px rgba(48, 83, 151, 0.15);
}

.dc-navy-btn:hover {
  background-color: #25427a !important;
  border-color: #25427a !important;
  color: #ffffff !important;
}

.btn-copied {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: #ffffff !important;
}

@media (max-width: 576px) {
  .dc-rounded-input {
    font-size: 12px !important;
    height: 42px !important;
  }

  .dc-navy-btn {
    height: 42px !important;
    padding: 0 12px !important;
    font-size: 13px !important;
  }

  .dc-card-title {
    font-size: 17px;
  }

  .dc-event-desc {
    min-height: auto;
  }
}
</style>
